import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Play, Pause, Rewind, FastForward, Download, ArrowLeft, Volume2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (s) => {
    if (!s || isNaN(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
};

export default function AudiobookPlayer() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const productId = params.get('product_id');
    const audioRef = useRef(null);
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState('');
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.9);
    const [downloading, setDownloading] = useState(false);
    const [downloadInfo, setDownloadInfo] = useState(null);

    useEffect(() => {
        if (!productId) { setError('No audiobook selected.'); return; }
        axios.get(`${API}/audiobook/${productId}/stream-meta`, { withCredentials: true })
            .then(r => { setMeta(r.data); setDownloadInfo({ used: r.data.downloads_used, limit: r.data.download_limit }); })
            .catch(e => setError(e.response?.data?.detail || 'Could not load audiobook.'));
    }, [productId]);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onTime = () => setProgress(a.currentTime);
        const onMeta = () => setDuration(a.duration);
        const onEnd = () => setPlaying(false);
        a.addEventListener('timeupdate', onTime);
        a.addEventListener('loadedmetadata', onMeta);
        a.addEventListener('ended', onEnd);
        return () => { a.removeEventListener('timeupdate', onTime); a.removeEventListener('loadedmetadata', onMeta); a.removeEventListener('ended', onEnd); };
    }, [meta]);

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
    };
    const skip = (delta) => { const a = audioRef.current; if (a) a.currentTime = Math.max(0, Math.min((a.duration||0), a.currentTime + delta)); };
    const seek = (e) => { const a = audioRef.current; if (a && duration) a.currentTime = (e.target.value / 100) * duration; };
    const setVol = (v) => { const a = audioRef.current; if (a) { a.volume = v; setVolume(v); } };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const r = await axios.post(`${API}/audiobook/${productId}/download`, {}, { withCredentials: true });
            setDownloadInfo({ used: r.data.downloads_used, limit: r.data.download_limit });
            // Derive extension from URL or mime
            const url = r.data.audio_url || '';
            const urlExt = (url.split('?')[0].split('#')[0].split('.').pop() || '').toLowerCase();
            const mimeExt = ({ 'audio/mpeg': 'mp3', 'audio/mp4': 'm4a', 'audio/wav': 'wav' }[(meta.audio_mime || '').toLowerCase()]) || '';
            const ext = ['mp3', 'm4a', 'm4b', 'wav'].includes(urlExt) ? urlExt : (mimeExt || 'mp3');
            const link = document.createElement('a');
            link.href = url;
            link.download = `${meta.title || 'audiobook'}.${ext}`;
            link.target = '_blank';
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
        } catch (e) {
            alert(e.response?.data?.detail || 'Download failed.');
        }
        setDownloading(false);
    };

    if (error) {
        return (
            <div className="min-h-screen bg-navy flex items-center justify-center p-6">
                <div className="bg-ivory rounded-lg p-8 text-center max-w-md">
                    <p className="font-heading text-2xl text-navy mb-4">Not Available</p>
                    <p className="font-body text-navy/70 mb-6" data-testid="audiobook-error">{error}</p>
                    <button onClick={() => navigate('/')} className="bg-gold text-navy font-bold px-6 py-3 rounded font-body">Go Home</button>
                </div>
            </div>
        );
    }
    if (!meta) return <div className="min-h-screen bg-navy flex items-center justify-center text-gold font-body">Loading audiobook…</div>;

    const pct = duration ? (progress / duration) * 100 : 0;
    const remaining = downloadInfo ? downloadInfo.limit - downloadInfo.used : 100;
    const atLimit = remaining <= 0;

    return (
        <div className="min-h-screen bg-navy text-white flex flex-col" data-testid="audiobook-player">
            <div className="bg-navy border-b border-gold/20">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gold hover:text-white font-body" data-testid="audiobook-back-btn">
                        <ArrowLeft size={20}/> Home
                    </button>
                    <p className="font-heading text-gold text-lg font-bold">KNOW YOUR STUFF · Audiobook</p>
                    <div className="w-16"></div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-2xl w-full bg-navy border border-gold/30 rounded-lg p-8 shadow-2xl shadow-gold/10 space-y-8">
                    <div className="flex flex-col md:flex-row items-center gap-6">
                        {meta.cover_url && (
                            <img src={meta.cover_url} alt="Cover" className="w-40 h-40 object-cover rounded-lg border border-gold/30 shadow-lg" data-testid="audiobook-cover"/>
                        )}
                        <div className="text-center md:text-left flex-1">
                            <p className="font-body text-gold uppercase tracking-wider text-xs mb-2">Audiobook</p>
                            <h1 className="font-heading text-3xl md:text-4xl text-white font-bold mb-1" data-testid="audiobook-title">{meta.title}</h1>
                            <p className="font-body text-white/60 italic">by Alice M. White, B.S., MBA</p>
                        </div>
                    </div>

                    <audio ref={audioRef} src={meta.audio_url} preload="metadata" data-testid="audiobook-audio-element"/>

                    <div className="space-y-2">
                        <input type="range" min="0" max="100" step="0.1" value={pct} onChange={seek}
                            className="w-full accent-gold" data-testid="audiobook-scrub"/>
                        <div className="flex items-center justify-between font-body text-sm text-white/70">
                            <span data-testid="audiobook-current-time">{fmt(progress)}</span>
                            <span data-testid="audiobook-duration">{fmt(duration)}</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-center gap-4">
                        <button onClick={() => skip(-15)} className="p-3 rounded-full bg-navy border border-gold/40 text-gold hover:bg-gold/10 transition-colors" title="Rewind 15s" data-testid="audiobook-rewind">
                            <Rewind size={22}/>
                        </button>
                        <button onClick={toggle} className="p-5 rounded-full bg-gold text-navy hover:bg-gold-hover shadow-lg" data-testid="audiobook-play">
                            {playing ? <Pause size={32}/> : <Play size={32}/>}
                        </button>
                        <button onClick={() => skip(30)} className="p-3 rounded-full bg-navy border border-gold/40 text-gold hover:bg-gold/10 transition-colors" title="Forward 30s" data-testid="audiobook-forward">
                            <FastForward size={22}/>
                        </button>
                    </div>

                    <div className="flex items-center gap-3">
                        <Volume2 size={18} className="text-gold"/>
                        <input type="range" min="0" max="1" step="0.01" value={volume} onChange={e => setVol(parseFloat(e.target.value))} className="flex-1 accent-gold" data-testid="audiobook-volume"/>
                    </div>

                    <div className="pt-4 border-t border-gold/20 text-center space-y-2">
                        <button onClick={handleDownload} disabled={downloading || atLimit}
                            className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-body font-bold transition-colors ${atLimit ? 'bg-navy border border-red-500/40 text-red-300 cursor-not-allowed' : 'bg-ivory text-navy hover:bg-white'} disabled:opacity-60`}
                            data-testid="audiobook-download-btn">
                            <Download size={18}/> {atLimit ? 'Download limit reached' : (downloading ? 'Downloading…' : 'Download Audio')}
                        </button>
                        {downloadInfo && (
                            <p className="font-body text-white/50 text-xs" data-testid="audiobook-download-count">
                                {downloadInfo.used} / {downloadInfo.limit} downloads used
                                {atLimit && ' — you can still stream unlimited times in this player.'}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
